import PropTypes from 'prop-types'
import { sliceStatusPropTypes } from '../slice.types'

/**
 * @typedef {Object} ShippingRate
 * @property {string} type - Rate type (e.g., "dimensional")
 * @property {string} service_name - Service name (e.g., "USPS Ground Advantage")
 * @property {string} carrier_name - Carrier name (e.g., "Stamps.com", "FedEx", "UPS")
 * @property {string} service_code - Service code identifier
 * @property {number} cost - Shipping cost in cents
 * @property {number} estimated_days - Estimated delivery time in days
 * @property {number} carrier_service_id - Carrier service record identifier
 * @property {string} encrypted_cost - Encrypted cost value for security
 */

/**
 * @typedef {Object} LineItem
 * @property {number} cart_item_id - Cart item ID
 * @property {number} quantity - Quantity for this shipment group
 * @property {number} [customer_address_id] - Customer address ID
 */

/**
 * @typedef {Object} DestinationAddress
 * @property {number} [id] - Address ID
 * @property {string} [full_name] - Full name
 * @property {string} [address_line_1] - Address line 1
 * @property {string} [address_line_2] - Address line 2
 * @property {string} [city] - City
 * @property {string} [state] - State
 * @property {string} [zip] - ZIP code
 */

/**
 * @typedef {Object} ShippingGroup
 * @property {string} carrier_service_group_id - Comma-separated list of service group IDs
 * @property {LineItem[]} line_items - Line items with quantities for this group
 * @property {DestinationAddress} [destination_address] - Destination address for this group
 * @property {ShippingRate[]} rates - Available shipping rates for this group
 */

/**
 * @typedef {Object} CalculationProgress
 * @property {number} completedGroups - Number of groups that have completed calculation
 * @property {number} totalGroups - Total number of groups to calculate
 * @property {number} progressPercent - Percentage of completion (0-100)
 */

/**
 * @typedef {Object} CheckoutRatesState
 * @property {ShippingGroup[]} rates - Available shipping groups with rates
 * @property {ShippingRate|null} selectedRate - Currently selected shipping rate (legacy)
 * @property {Object.<string, {service_code: string, cost_in_cents: number, encrypted_cost: string, carrier_service_id: number, cart_item_ids: number[], customer_address_id: number}>} selectedRatesByGroup - Selected rates per group
 * @property {string|null} calculationId - ID of current async rate calculation
 * @property {string|null} shippingRateLogId - ID of shipping rate log record for this calculation
 * @property {boolean} isCalculating - Whether rates are currently being calculated
 * @property {CalculationProgress|null} calculationProgress - Progress of async calculation
 * @property {string|null} configurationId - ID of shipping group configuration
 */

/** @typedef {import('../slice.types').BaseSlice & CheckoutRatesState} CheckoutRatesSlice */

/** @type {CheckoutRatesSlice} */
export const initialCheckoutRatesState = {
  status: 'init',
  errors: [],
  rates: [],
  selectedRate: null,
  selectedRatesByGroup: {},
  calculationId: null,
  shippingRateLogId: null,
  isCalculating: false,
  calculationProgress: null,
  configurationId: null
}

/** @enum {string} */
export const CHECKOUT_RATES_ACTIONS = {
  SET_CHECKOUT_RATES_STATUS: 'SET_CHECKOUT_RATES_STATUS',
  SET_CHECKOUT_RATES_ERRORS: 'SET_CHECKOUT_RATES_ERRORS',
  SET_CHECKOUT_RATES_RATES: 'SET_CHECKOUT_RATES_RATES',
  SET_CHECKOUT_RATES_SELECTED_RATE: 'SET_CHECKOUT_RATES_SELECTED_RATE',
  SET_SELECTED_RATE_FOR_GROUP: 'SET_SELECTED_RATE_FOR_GROUP',

  // Async calculation actions
  SET_CALCULATION_ID: 'SET_CALCULATION_ID',
  SET_SHIPPING_RATE_LOG_ID: 'SET_SHIPPING_RATE_LOG_ID',
  SET_IS_CALCULATING: 'SET_IS_CALCULATING',
  SET_CALCULATION_PROGRESS: 'SET_CALCULATION_PROGRESS',
  SET_CONFIGURATION_ID: 'SET_CONFIGURATION_ID',

  CLEAR_CHECKOUT_RATES_ERRORS: 'CLEAR_CHECKOUT_RATES_ERRORS',
  CLEAR_CHECKOUT_RATES_RATES: 'CLEAR_CHECKOUT_RATES_RATES',
  CLEAR_CHECKOUT_RATES_SELECTED_RATE: 'CLEAR_CHECKOUT_RATES_SELECTED_RATE'
}

export const shippingRatePropType = PropTypes.shape({
  cost_in_cents: PropTypes.number.isRequired,
  carrier: PropTypes.string.isRequired
})

export const checkoutRatesSlicePropType = PropTypes.shape({
  status: sliceStatusPropTypes,
  errors: PropTypes.arrayOf(PropTypes.shape({ message: PropTypes.string })),
  rates: PropTypes.arrayOf(shippingRatePropType),
  selectedRate: shippingRatePropType
})

/**
 * Base slice actions
 * @typedef {{ type: CHECKOUT_RATES_ACTIONS, payload: CheckoutRatesSlice['status'] }} CheckoutRatesStatusAction
 * @typedef {{ type: CHECKOUT_RATES_ACTIONS, payload: CheckoutRatesSlice['errors'] }} CheckoutRatesErrorsAction
 *
 * Checkout rates slice actions
 * @typedef {{ type: CHECKOUT_RATES_ACTIONS, payload: CheckoutRatesSlice['rates'] }} CheckoutRatesRatesAction
 * @typedef {{ type: CHECKOUT_RATES_ACTIONS, payload: CheckoutRatesSlice['selectedRate'] }} CheckoutRatesSelectedRateAction
 *
 * Clear action
 * @typedef {{ type: CHECKOUT_RATES_ACTIONS }} CheckoutRatesClearAction
 *
 * @typedef {CheckoutRatesStatusAction | CheckoutRatesErrorsAction | CheckoutRatesRatesAction | CheckoutRatesSelectedRateAction | CheckoutRatesClearAction} CheckoutRatesAction
 * */
